import { useState, useEffect } from 'react';
import {
  Users, UserRound, Car, Bus, Map, GraduationCap, Tags, BarChart3, Download,
  Wallet, Megaphone, CalendarClock, BookOpen, PartyPopper, Siren, School,
  LayoutDashboard, ClipboardList, FileSpreadsheet, NotebookPen, IdCard,
  BookMarked, ListChecks, Coins, Landmark, Library, DoorOpen, CalendarX, BedDouble, Package, BadgeIndianRupee,
  FileUp, CalendarRange, FileBadge,
} from 'lucide-react';
import { loginSchoolAdmin } from './api/client';
import './App.css';
import Logo from './ui/Logo';
import Button from './ui/Button';
import Sidebar from './ui/Sidebar';
import TopBar from './ui/TopBar';
import StudentsPanel      from './components/StudentsPanel';
import GuardiansPanel     from './components/GuardiansPanel';
import DriversPanel       from './components/DriversPanel';
import VansPanel          from './components/VansPanel';
import RoutesPanel        from './components/RoutesPanel';
import SchoolProfilePanel from './components/SchoolProfilePanel';
import SosBanner          from './components/SosBanner';
import SosAlertsPage      from './components/SosAlertsPage';
import StaffPanel         from './components/StaffPanel';
import ClassesPanel       from './components/ClassesPanel';
import StaffAttendanceDashboard from './components/StaffAttendanceDashboard';
import AnnouncementsPanel from './components/AnnouncementsPanel';
import LeavePanel          from './components/LeavePanel';
import TimetablePanel      from './components/TimetablePanel';
import HolidaysPanel       from './components/HolidaysPanel';
import AttendanceReportsPanel from './components/AttendanceReportsPanel';
import FeesPanel           from './components/FeesPanel';
import HomeDashboard       from './components/HomeDashboard';
import StudentProfile      from './components/StudentProfile';
import GlobalSearch        from './components/GlobalSearch';
import ReportsPanel        from './components/ReportsPanel';
import EnquiriesPanel      from './components/EnquiriesPanel';
import ExamsPanel          from './components/ExamsPanel';
import IdCardsPanel        from './components/IdCardsPanel';
import HomeworkPanel       from './components/HomeworkPanel';
import SyllabusPanel       from './components/SyllabusPanel';
import FeeStructurePanel   from './components/FeeStructurePanel';
import VisitorsPanel       from './components/VisitorsPanel';
import AccountsPanel       from './components/AccountsPanel';
import LibraryPanel        from './components/LibraryPanel';
import StudentLeavePanel   from './components/StudentLeavePanel';
import HostelPanel         from './components/HostelPanel';
import InventoryPanel      from './components/InventoryPanel';
import PayrollPanel        from './components/PayrollPanel';
import ImportPanel         from './components/ImportPanel';
import AcademicYearPanel   from './components/AcademicYearPanel';
import CertificatesPanel   from './components/CertificatesPanel';
import AdmitCardsPanel     from './components/AdmitCardsPanel';
import TransportPanel      from './components/TransportPanel';

// { section } entries are sidebar group headings (see ui/Sidebar.jsx).
const SCHOOL_TABS = [
  { key: 'home',        label: 'Home',        icon: LayoutDashboard },
  { section: 'People' },
  { key: 'students',    label: 'Students',    icon: Users },
  { key: 'guardians',   label: 'Guardians',   icon: UserRound },
  { key: 'staff',       label: 'Staff',       icon: GraduationCap },
  { key: 'classes',     label: 'Classes',     icon: Tags },
  { key: 'idCards',     label: 'ID Cards',    icon: IdCard },
  { key: 'import',      label: 'Bulk Import', icon: FileUp },
  { key: 'academicYear', label: 'Academic Year & Promotion', icon: CalendarRange },
  { section: 'Front office' },
  { key: 'enquiries',   label: 'Admissions & Enquiries', icon: ClipboardList },
  { key: 'visitors',    label: 'Visitor Register', icon: DoorOpen },
  { key: 'certificates', label: 'Certificates', icon: FileBadge },
  { section: 'Academics' },
  { key: 'homework',    label: 'Homework',    icon: BookMarked },
  { key: 'syllabus',    label: 'Syllabus',    icon: ListChecks },
  { key: 'exams',       label: 'Exams & Results', icon: NotebookPen },
  { key: 'admitCards',  label: 'Admit Cards', icon: IdCard },
  { key: 'timetable',   label: 'Timetable',   icon: BookOpen },
  { key: 'library',     label: 'Library',     icon: Library },
  { key: 'holidays',    label: 'Holidays',    icon: PartyPopper },
  { section: 'Attendance' },
  { key: 'staffAttendance', label: 'Attendance Dashboard', icon: BarChart3 },
  { key: 'attendanceReports', label: 'Attendance Reports', icon: Download },
  { key: 'studentLeave', label: 'Student Leave', icon: CalendarX },
  { key: 'leave',       label: 'Staff Leave Requests', icon: CalendarClock },
  { section: 'Finance' },
  { key: 'fees',        label: 'Fees',        icon: Wallet },
  { key: 'feeStructure', label: 'Fee Structure', icon: Coins },
  { key: 'payroll',     label: 'HR & Payroll', icon: BadgeIndianRupee },
  { key: 'accounts',    label: 'Income & Expenses', icon: Landmark },
  { key: 'reports',     label: 'Reports',     icon: FileSpreadsheet },
  { section: 'Operations' },
  { key: 'hostel',      label: 'Hostel',      icon: BedDouble },
  { key: 'inventory',   label: 'Inventory',   icon: Package },
  { section: 'Communication' },
  { key: 'announcements', label: 'Notifications', icon: Megaphone },
  { section: 'Transport' },
  { key: 'transport',   label: 'Assign Students', icon: Bus },
  { key: 'drivers',     label: 'Drivers',     icon: Car },
  { key: 'vans',        label: 'Vans',        icon: Bus },
  { key: 'routes',      label: 'Routes',      icon: Map },
  { key: 'sos',         label: 'SOS Alerts',  icon: Siren },
  { section: 'School' },
  { key: 'profile',     label: 'School Profile', icon: School },
];

export default function App() {
  const [token,      setToken]      = useState(() => localStorage.getItem('school_token'));
  const [schoolName, setSchoolName] = useState(() => localStorage.getItem('school_name') || '');
  const [board,      setBoard]      = useState(() => localStorage.getItem('school_board') || 'CBSE');
  const [active,     setActive]     = useState('home');
  const [subTab,     setSubTab]     = useState(null);   // e.g. 'dues' for navigate('reports:dues')
  const [profile,    setProfile]    = useState(null);   // { id, tab } while a student profile is open
  const [editStudentId, setEditStudentId] = useState(null);
  const [admitFrom,  setAdmitFrom]  = useState(null);   // enquiry being turned into a student
  const [certStudent, setCertStudent] = useState(null); // student pre-selected on the Certificates screen

  // Login form state
  const [code,       setCode]       = useState('');
  const [loginId,    setLoginId]    = useState('');
  const [password,   setPassword]   = useState('');
  const [loginErr,   setLoginErr]   = useState('');
  const [logging,    setLogging]    = useState(false);
  const [paymentReminder, setPaymentReminder] = useState(null);

  useEffect(() => {
    if (token) localStorage.setItem('school_token', token);
  }, [token]);

  // ── Named admin login (school code + login_id + password) ───
  async function handleAdminLogin(e) {
    e.preventDefault();
    setLogging(true); setLoginErr('');
    try {
      const data = await loginSchoolAdmin(code.trim(), loginId.trim(), password.trim());
      localStorage.removeItem('super_admin_token');
      localStorage.setItem('school_token', data.token);
      localStorage.setItem('school_name',  data.school.name);
      localStorage.setItem('school_board', data.school.board || 'CBSE');
      setToken(data.token);
      setSchoolName(data.school.name);
      setBoard(data.school.board || 'CBSE');
      if (data.payment_reminder) setPaymentReminder(data.payment_reminder);
    } catch (err) {
      setLoginErr(err.response?.data?.error || 'Login failed');
    } finally { setLogging(false); }
  }

  function handleLogout() {
    localStorage.removeItem('school_token');
    localStorage.removeItem('school_name');
    localStorage.removeItem('school_board');
    setToken(null); setSchoolName(''); setBoard('CBSE');
    setActive('home'); setProfile(null);
  }

  // navigate('reports:dues') → Reports panel opened on its Dues tab.
  function navigate(target) {
    const [key, sub] = target.split(':');
    setActive(key); setSubTab(sub || null); setProfile(null);
  }

  function openStudent(id, tab = 'attendance') {
    setProfile({ id, tab });
    window.scrollTo({ top: 0 });
  }

  // ── Not logged in ────────────────────────────────────────────
  if (!token) {
    return (
      <div className="app-login-page">
        <div className="sun-glow" />
        <div className="cloud cloud-1" /><div className="cloud cloud-2" /><div className="cloud cloud-3" />
        <span className="floaty floaty-1">🎒</span>
        <span className="floaty floaty-2">📚</span>
        <span className="floaty floaty-3">✏️</span>
        <span className="floaty floaty-4">🚸</span>

        <div className="relative z-[2] min-h-screen flex items-center justify-center px-6 pb-[70px]">
          <div className="w-full max-w-[400px] bg-white/90 backdrop-blur-md rounded-l shadow-2 border border-white/60 p-9">
            <div className="flex justify-center mb-5"><Logo size={56} /></div>
            <h1 className="font-display font-extrabold text-2xl text-center text-ink mb-1">Zorvexl School Admin</h1>
            <p className="text-sm text-center text-ink-soft mb-6">Students, fees, exams, attendance and safe school transport — in one place</p>

            {loginErr && <div className="bg-critical-tint border border-critical/20 text-critical text-sm px-3 py-2.5 rounded-s mb-3.5">{loginErr}</div>}

            <form onSubmit={handleAdminLogin} className="flex flex-col gap-1">
              <label className="text-[11px] font-bold uppercase tracking-wide text-ink-soft mt-2">School Code</label>
              <input className="border border-line rounded-s px-3 py-2.5 text-[15px] bg-paper focus:outline-none focus:ring-2 focus:ring-transit focus:bg-white mb-1"
                placeholder="e.g. GRWOOD" value={code}
                onChange={e => setCode(e.target.value.toUpperCase())} required />
              <label className="text-[11px] font-bold uppercase tracking-wide text-ink-soft mt-2">Login ID</label>
              <input className="border border-line rounded-s px-3 py-2.5 text-[15px] bg-paper focus:outline-none focus:ring-2 focus:ring-transit focus:bg-white mb-1"
                placeholder="e.g. admin.greenwood" value={loginId}
                onChange={e => setLoginId(e.target.value)} required />
              <label className="text-[11px] font-bold uppercase tracking-wide text-ink-soft mt-2">Password</label>
              <input className="border border-line rounded-s px-3 py-2.5 text-[15px] bg-paper focus:outline-none focus:ring-2 focus:ring-transit focus:bg-white mb-2"
                type="password" value={password}
                onChange={e => setPassword(e.target.value)} required />
              <Button disabled={logging} className="w-full mt-2">{logging ? 'Logging in…' : 'Login'}</Button>
            </form>
          </div>
        </div>

        <div className="road"><span className="bus">🚌</span></div>
      </div>
    );
  }

  // ── Logged in ────────────────────────────────────────────────
  return (
    <div className="flex min-h-screen bg-paper">
      {paymentReminder && (
        <div style={styles.reminderOverlay} onClick={() => setPaymentReminder(null)}>
          <div style={styles.reminderModal} onClick={e => e.stopPropagation()}>
            <h3 style={{ marginTop: 0 }}>💰 Payment Reminder</h3>
            <p style={{ whiteSpace: 'pre-wrap' }}>{paymentReminder}</p>
            <button onClick={() => setPaymentReminder(null)} style={styles.reminderDismissBtn}>Dismiss</button>
          </div>
        </div>
      )}

      <Sidebar items={SCHOOL_TABS} active={active} onSelect={navigate} tier="transit" />

      <div className="flex-1 flex flex-col min-w-0">
        <SosBanner />
        <TopBar title={schoolName} tierLabel="School Admin" tier="transit" onLogout={handleLogout}>
          <GlobalSearch onOpenStudent={openStudent} />
        </TopBar>
        <div className="p-6 overflow-x-auto">
          {profile ? (
            <StudentProfile
              studentId={profile.id}
              initialTab={profile.tab}
              schoolName={schoolName}
              board={board}
              onBack={() => setProfile(null)}
              onOpenStudent={openStudent}
              onEdit={(id) => { setProfile(null); setActive('students'); setEditStudentId(id); }}
              onCertificate={(id) => { setProfile(null); setCertStudent(id); setActive('certificates'); }}
            />
          ) : <>
          {active === 'home'       && <HomeDashboard onNavigate={navigate} />}
          {active === 'students'   && <StudentsPanel onOpenStudent={openStudent} editId={editStudentId} onEditHandled={() => setEditStudentId(null)}
                                        admitFrom={admitFrom} onAdmitHandled={() => setAdmitFrom(null)} onImport={() => navigate('import')} />}
          {active === 'import'     && <ImportPanel schoolName={schoolName} onDone={() => navigate('students')} />}
          {active === 'academicYear' && <AcademicYearPanel onOpenStudent={openStudent} />}
          {active === 'certificates' && <CertificatesPanel initialStudentId={certStudent} onOpenStudent={openStudent} />}
          {active === 'enquiries'  && <EnquiriesPanel onAdmit={(en) => { setAdmitFrom(en); setActive('students'); }} />}
          {active === 'visitors'   && <VisitorsPanel />}
          {active === 'homework'   && <HomeworkPanel />}
          {active === 'syllabus'   && <SyllabusPanel />}
          {active === 'library'    && <LibraryPanel />}
          {active === 'studentLeave' && <StudentLeavePanel onOpenStudent={openStudent} />}
          {active === 'feeStructure' && <FeeStructurePanel />}
          {active === 'accounts'   && <AccountsPanel />}
          {active === 'payroll'    && <PayrollPanel schoolName={schoolName} />}
          {active === 'hostel'     && <HostelPanel onOpenStudent={openStudent} />}
          {active === 'inventory'  && <InventoryPanel />}
          {active === 'idCards'    && <IdCardsPanel onOpenStudent={openStudent} />}
          {active === 'exams'      && <ExamsPanel onOpenStudent={openStudent} schoolName={schoolName} board={board} />}
          {active === 'admitCards' && <AdmitCardsPanel onOpenStudent={openStudent} />}
          {active === 'reports'    && <ReportsPanel initialTab={subTab || 'collection'} onOpenStudent={openStudent} schoolName={schoolName} />}
          {active === 'guardians'  && <GuardiansPanel  />}
          {active === 'transport'  && <TransportPanel onOpenStudent={openStudent} />}
          {active === 'drivers'    && <DriversPanel    />}
          {active === 'vans'       && <VansPanel       />}
          {active === 'routes'     && <RoutesPanel     />}
          {active === 'staff'      && <StaffPanel      />}
          {active === 'classes'    && <ClassesPanel    />}
          {active === 'staffAttendance' && <StaffAttendanceDashboard />}
          {active === 'attendanceReports' && <AttendanceReportsPanel />}
          {active === 'fees'       && <FeesPanel onOpenStudent={openStudent} />}
          {active === 'announcements'   && <AnnouncementsPanel />}
          {active === 'leave'      && <LeavePanel      />}
          {active === 'timetable'  && <TimetablePanel  />}
          {active === 'holidays'   && <HolidaysPanel   />}
          {active === 'sos'        && <SosAlertsPage   />}
          {active === 'profile'    && <SchoolProfilePanel />}
          </>}
        </div>
      </div>
    </div>
  );
}

const styles = {
  reminderOverlay: { position: 'fixed', inset: 0, background: 'rgba(16,27,34,0.5)', display: 'flex',
                      alignItems: 'center', justifyContent: 'center', zIndex: 2000 },
  reminderModal:   { background: '#fff', padding: 24, borderRadius: 12, width: 420, maxWidth: '90vw',
                      boxShadow: 'var(--shadow-2)' },
  reminderDismissBtn: { background: 'var(--transit)', color: '#fff', border: 'none', padding: '8px 16px',
                         borderRadius: 6, cursor: 'pointer', marginTop: 8, fontWeight: 700 },
};
